import { useMemo, useState } from 'react';
import TextInput, { TextInputProps } from '@/components/ui/Form/TextInput';
import { InputError } from '@/components/ui/Form/Input';
import {
  formatPhoneForDisplay,
  normalizePhoneForSubmit,
  isValidPhone
} from '@/components/ui/Form/Utils/phone';

export type PhoneInputProps = Omit<
  TextInputProps,
  | 'type'
  | 'value'
  | 'onChange'
  | 'acceptLetters'
  | 'acceptNumbers'
  | 'acceptSpaces'
  | 'acceptSpecialChars'
  | 'allowedChars'
> & {
  name: string;
  value?: string;
  onChange?: (displayValue: string) => void;
  onNormalizedChange?: (normalizedValue: string) => void;
  invalidMessage?: string;
};

const PhoneInput = ({
  name,
  label,
  required = false,
  value: controlledValue,
  onChange,
  onNormalizedChange,
  invalidMessage = 'Please enter a valid phone',
  errors,
  placeholder,
  ...rest
}: PhoneInputProps): JSX.Element => {
  const [internalValue, setInternalValue] = useState('');
  const isControlled = controlledValue !== undefined;
  const displayValue = isControlled ? controlledValue : internalValue;

  const normalizedValue = useMemo(() => normalizePhoneForSubmit(displayValue), [displayValue]);

  const handleChange = (raw: string) => {
    const formatted = formatPhoneForDisplay(raw);
    if (!isControlled) {
      setInternalValue(formatted);
    }
    onChange?.(formatted);
    onNormalizedChange?.(normalizePhoneForSubmit(formatted));
  };

  const phoneErrors = useMemo((): InputError[] => {
    const result: InputError[] = [];

    if (required) {
      result.push({
        message: invalidMessage,
        match: (value: string) => !isValidPhone(value)
      });
    } else {
      result.push({
        message: invalidMessage,
        match: (value: string) => value.trim().length > 0 && !isValidPhone(value)
      });
    }

    if (errors) result.push(...errors);
    return result;
  }, [required, invalidMessage, errors]);

  return (
    <>
      <TextInput
        {...rest}
        label={label}
        name={`${name}-display`}
        type="tel"
        value={displayValue}
        placeholder={placeholder || '+1 (XXX) XXX-XXXX'}
        required={required}
        onChange={handleChange}
        errors={phoneErrors}
        errorValidationEnabled={false}
        inputProps={{ required: false }}
      />
      <input type="hidden" name={name} value={normalizedValue} readOnly />
    </>
  );
};

export default PhoneInput;
